import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { shareOrigin } from "../lib/api";
import BottomNav from "../components/BottomNav";
import { Crown, Check, Sparkles, ArrowLeft, Smartphone, Users } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  {
    id: "basic",
    name: "Basic",
    tagline: "Solo · Stay close to one device",
    devices: 1,
    monthly: { id: "basic_monthly", price: "0.99" },
    yearly:  { id: "basic_yearly",  price: "9.99", save: "Save 16%" },
    features: [
      "1 connected device",
      "Real-time location sharing",
      "3-mile proximity alerts",
      "Find Me radar",
      "30-day location history",
    ],
  },
  {
    id: "plus",
    name: "Proximity Plus",
    tagline: "Family · Up to 5 devices on one plan",
    devices: 5,
    highlight: true,
    monthly: { id: "plus_monthly", price: "3.99" },
    yearly:  { id: "plus_yearly",  price: "39.99", save: "Save 17%" },
    features: [
      "Up to 5 connected devices",
      "Master/Node device management",
      "Invite by link or 6-digit code",
      "Unlimited safe-zone geofences",
      "Parental dashboard",
      "90-day location history",
    ],
  },
  {
    id: "elite",
    name: "Proximity Elite",
    tagline: "Group · Up to 65 devices",
    devices: 65,
    monthly: { id: "elite_monthly", price: "49.99" },
    yearly:  { id: "elite_yearly",  price: "499.00", save: "Save 17%" },
    features: [
      "Up to 65 connected devices",
      "Built for teams, congregations, large families",
      "All Plus features included",
      "Priority background push",
      "Future Command Center access",
    ],
  },
];

export default function Billing() {
  const [search, setSearch] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [polling, setPolling] = useState(false);
  const [interval, setInterval] = useState("month"); // month | year
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing/status");
      setStatus(r.data);
      if (r.data?.is_node) {
        // Nodes don't manage billing — bounce
        navigate("/settings", { replace: true });
      }
    } catch {}
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Scroll-target a tier via ?tier=elite query
  useEffect(() => {
    const t = search.get("tier");
    if (!t) return;
    const el = document.getElementById(`tier-${t}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [search, loading]);

  // Poll after returning from Stripe
  useEffect(() => {
    const sid = search.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const max = 8;
    const tick = async () => {
      attempts++;
      try {
        const r = await api.get(`/billing/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Payment received — you're all set!");
          setSearch({});
          setPolling(false);
          load();
          return;
        }
      } catch {}
      if (attempts >= max) {
        toast.message("Still processing — refresh in a moment.");
        setPolling(false);
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
  }, [search, setSearch, load]);

  const buy = async (packageId, tierId) => {
    // Pre-check downgrades
    const currentTier = status?.tier;
    const order = { basic: 1, plus: 2, elite: 3 };
    if (currentTier && order[tierId] < order[currentTier]) {
      try {
        const check = await api.post("/billing/check-downgrade", { tier: tierId });
        if (!check.data?.ok) {
          toast.error(check.data?.message || "Downgrade blocked");
          return;
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Downgrade check failed");
        return;
      }
    }
    setBuying(packageId);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: shareOrigin(),
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6 flex items-center gap-3">
        <Link to="/settings" className="size-9 rounded-full border border-white/10 grid place-items-center">
          <ArrowLeft size={16}/>
        </Link>
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Crown size={12}/> Membership</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-1">Choose your plan</h1>
        </div>
      </header>

      {loading ? (
        <div className="px-6 label-eyebrow">Loading…</div>
      ) : (
        <>
          {/* Status card */}
          <section className="px-6">
            <div className={`p-5 rounded-2xl border ${
              status?.is_paid ? "border-[#34C759]/30 bg-[#34C759]/8" :
              status?.in_trial ? "border-[#FFCC00]/30 bg-[#FFCC00]/8" :
              "border-[#FF3B30]/30 bg-[#FF3B30]/8"
            }`}>
              <div className="label-eyebrow">Current status</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1" data-testid="billing-status-label">
                {status?.is_paid ? `${status?.tier_label || "Active"} · ${status?.device_count || 1} of ${status?.max_devices || 1} devices` :
                 status?.in_trial ? `Free trial · ${status?.days_left_trial}d left` :
                 "No active plan"}
              </div>
              <div className="text-sm text-zinc-400 mt-2">
                {status?.is_paid ? `Renews on ${new Date(status?.paid_until).toLocaleDateString()}` :
                 status?.in_trial ? `Ends ${new Date(status?.trial_ends_at).toLocaleDateString()} — choose a plan anytime` :
                 "Pick a plan below to start tracking your circle."}
              </div>
            </div>
          </section>

          {/* Interval toggle */}
          <section className="px-6 mt-6">
            <div className="inline-flex p-1 rounded-full border border-white/10 bg-white/[0.03]">
              <button
                data-testid="interval-month"
                onClick={() => setInterval("month")}
                className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${interval === "month" ? "bg-[#007AFF] text-white" : "text-zinc-400"}`}
              >
                Monthly
              </button>
              <button
                data-testid="interval-year"
                onClick={() => setInterval("year")}
                className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${interval === "year" ? "bg-[#007AFF] text-white" : "text-zinc-400"}`}
              >
                Yearly · Save ~17%
              </button>
            </div>
          </section>

          {/* Tiers */}
          <section className="px-6 mt-5 grid grid-cols-1 lg:grid-cols-3 gap-3">
            {TIERS.map((t) => (
              <TierCard
                key={t.id}
                tier={t}
                interval={interval}
                isCurrent={status?.tier === t.id}
                busy={!!buying || polling}
                buying={buying}
                onBuy={(pkgId) => buy(pkgId, t.id)}
              />
            ))}
          </section>

          <div className="px-6 mt-8 text-xs text-zinc-600 text-center">
            Powered by Stripe. Cancel anytime. Downgrades require freeing up device slots first.
          </div>
        </>
      )}

      <BottomNav />
    </div>
  );
}

function TierCard({ tier, interval, isCurrent, busy, buying, onBuy }) {
  const pkg = interval === "year" ? tier.yearly : tier.monthly;
  const per = interval === "year" ? "/ year" : "/ month";
  const highlight = tier.highlight;
  return (
    <div
      id={`tier-${tier.id}`}
      data-testid={`tier-card-${tier.id}`}
      className={`relative p-5 rounded-2xl border flex flex-col ${
        highlight
          ? "border-[#007AFF]/40 bg-gradient-to-br from-[#007AFF]/15 to-transparent"
          : isCurrent
            ? "border-[#34C759]/40 bg-[#34C759]/5"
            : "border-white/10 bg-white/[0.025]"
      }`}
    >
      {highlight && !isCurrent && (
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-[#007AFF] text-[10px] font-black tracking-wider uppercase">
          Most popular
        </div>
      )}
      {isCurrent && (
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-[#34C759] text-[10px] font-black tracking-wider uppercase">
          Current
        </div>
      )}
      <div className="label-eyebrow">{tier.name}</div>
      <div className="text-xs text-zinc-500 mt-1">{tier.tagline}</div>

      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="font-display font-black text-4xl tracking-tighter">${pkg.price}</span>
        <span className="text-sm text-zinc-500">{per}</span>
      </div>
      {interval === "year" && pkg.save && (
        <div className="mt-1 text-[11px] font-bold text-[#34C759]">{pkg.save}</div>
      )}

      <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-zinc-400">
        <Smartphone size={12}/> {tier.devices} {tier.devices === 1 ? "device" : "devices"}
      </div>

      <ul className="mt-4 space-y-1.5 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-zinc-300">
            <Check size={13} className="text-[#34C759] mt-0.5 shrink-0"/>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`buy-${pkg.id}`}
        onClick={() => onBuy(pkg.id)}
        disabled={busy || isCurrent}
        className={`mt-5 font-bold text-sm rounded-full py-2.5 disabled:opacity-50 transition ${
          highlight ? "bg-[#007AFF] hover:bg-[#005ec4] text-white" : "border border-white/10 hover:bg-white/5"
        }`}
      >
        {isCurrent ? "You're on this plan" : (buying === pkg.id ? "Processing…" : `Choose ${tier.name}`)}
      </button>
    </div>
  );
}
